import { useMemo } from 'react';
import type { ReactNode } from 'react';

import { parseInlineMarkup, type ParseInlineMarkupOptions } from '@/utils/inlineMarkup';

/**
 * Hook that parses inline markdown formatting and returns memoized React nodes.
 * Supports: bold (**), italic (* *), underline (__), strikethrough (~~),
 * small text (;;), and links ([text](url)).
 *
 * @param text - The markdown text to parse
 * @param options - Optional settings for formatting
 * @param options.size - Text size prop to apply to formatted Text components
 * @returns Memoized array of React nodes with appropriate formatting
 *
 * @example
 * ```tsx
 * function MyComponent({ description }: { description: string }) {
 *   const formattedDescription = useInlineMarkup(description, { size: 'sm' });
 *
 *   return <Text>{formattedDescription}</Text>;
 * }
 * ```
 */
export function useInlineMarkup(
  text: string | undefined | null,
  options?: Omit<ParseInlineMarkupOptions, 'parentClasses'>
): ReactNode[] {
  return useMemo(() => {
    if (!text) {
      return [];
    }

    return parseInlineMarkup(text, options);
  }, [text, options?.size]);
}
